import type { DocumentItem } from '@odo/shared-ui';
import React, { useEffect, useRef, useState } from 'react';
import clipboardListIcon from '../../assets/icons/clipboard-list.svg';
import DocumentViewerModal from '../modals/DocumentViewerModal';

interface IconsRowProps {
    // All docs per type: GCS versions + Monday files, pre-resolved by useOverviewDocAssets
    dxReportDocs?: DocumentItem[];
    sdiDocs?: DocumentItem[];
    rdiDocs?: DocumentItem[];
}

const ICON_BUTTON_BASE = 'rounded-lg items-center w-9 h-9 flex justify-center transition-colors';

export const IconsRow: React.FC<IconsRowProps> = ({
    dxReportDocs = [],
    sdiDocs = [],
    rdiDocs = []
}) => {
    const [docsOpen, setDocsOpen] = useState(false);
    const [viewerState, setViewerState] = useState<{
        title: string;
        documents: DocumentItem[];
    } | null>(null);
    const docsRef = useRef<HTMLDivElement>(null);

    const openDocument = (label: string, documents: DocumentItem[]) => {
        if (documents.length === 0) return;
        setViewerState({ title: label, documents });
        setDocsOpen(false);
    };

    // Close the dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (docsRef.current && !docsRef.current.contains(event.target as Node)) {
                setDocsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const docs = [
        { label: 'Diagnostic report', documents: dxReportDocs },
        { label: 'Start Day instructions', documents: sdiDocs },
        { label: 'Refinement Day instructions', documents: rdiDocs }
    ];

    const availableDocs = docs.filter((doc) => doc.documents.length > 0);

    return (
        <>
            <div className="flex gap-2">
                {/* Docs Button with Dropdown */}
                <div ref={docsRef} className="relative">
                    <button
                        onClick={() => setDocsOpen((open) => !open)}
                        disabled={availableDocs.length === 0}
                        className={`${ICON_BUTTON_BASE} ${
                            availableDocs.length === 0
                                ? 'opacity-50 cursor-not-allowed'
                                : docsOpen
                                  ? 'bg-gray-100'
                                  : 'hover:bg-gray-100'
                        }`}
                        title="View documents"
                    >
                        <img src={clipboardListIcon} alt="Documents" className="w-5 h-5" />
                    </button>

                    {docsOpen && availableDocs.length > 0 && (
                        <div className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-lg border border-gray-200 z-50 overflow-hidden">
                            <div className="py-1">
                                {docs.map((doc) => {
                                    const disabled = doc.documents.length === 0;
                                    return (
                                        <button
                                            key={doc.label}
                                            onClick={() => openDocument(doc.label, doc.documents)}
                                            disabled={disabled}
                                            className={`w-full flex items-center px-4 py-2.5 text-left transition-colors text-sm font-medium ${
                                                disabled
                                                    ? 'text-gray-300 cursor-not-allowed'
                                                    : 'text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            {doc.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {viewerState && (
                <DocumentViewerModal
                    title={viewerState.title}
                    documents={viewerState.documents}
                    onClose={() => setViewerState(null)}
                />
            )}
        </>
    );
};
